import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { Claim, Product } from "../api/models"
import { addEntityFetchReducers, EntityState } from "./entity"
import { logOut } from "./globalSlice"
import { RootState } from "./store"
import Api from "../api/api"
import { activePlansThunk } from "./plansSlice"
import { skuThunk } from "./skusSlice"
import { subscriptionThunk, switchSubscriptionThunk } from "./subscriptionSlice"
import { storeSkusThunk, storeSkuThunk } from "./storeSlice"
import { FetchableState, useFetchableState } from "./fetchableState"


interface ClaimsState extends EntityState<Claim> {
    bySku: {
        [skuId: string]: FetchableState<string[]>
    },
    debug: string
}

const initialState: ClaimsState = {
    entities: {},
    bySku: {},
    debug: "claims state"
}

// The store sku responses carry the latest version's claim set; index it by
// sku so user-facing surfaces (e.g. the upgrade popover) get per-sku claims
// without the admin claims endpoint.
const indexClaimsBySku = (state: ClaimsState, skuIds: string[], claims: Claim[]) => {
    skuIds.forEach(skuId => {
        const ids = claims.filter(claim => claim.skuId === skuId).map(claim => claim.id)
        const bySku = state.bySku[skuId]
        if (bySku === undefined)
            state.bySku[skuId] = { data: ids, currentRequestId: undefined, lastFetched: Date.now() }
        else {
            bySku.data = ids
            bySku.lastFetched = Date.now()
        }
    })
}

export const claimsSlice = createSlice({
    name: 'claims',
    initialState: initialState,
    reducers: {},
    extraReducers(builder) {
        addEntityFetchReducers<
            Claim,
            { claim: Claim, product?: Product },
            { id: string }
        >(
            builder,
            (payload) => payload.claim,
            claimThunk
        )
        builder.addCase(codeClaimsThunk.fulfilled, (state, action) => {
            const { claims } = action.payload
            claims.forEach(claim => {
                state.entities[claim.id] = {
                    data: claim,
                    lastFetched: Date.now()
                }
            })
        })
        builder.addCase(skuClaimsThunk.pending, (state, action) => {
            const skuId = action.meta.arg.skuId
            if (state.bySku[skuId] === undefined) {
                state.bySku[skuId] = {
                    data: undefined,
                    currentRequestId: action.meta.requestId,
                    lastFetched: undefined
                }
            } else {
                const skuClaims = state.bySku[skuId]
                if (skuClaims.currentRequestId)
                    return
                skuClaims.currentRequestId = action.meta.requestId
            }
        })
        builder.addCase(skuClaimsThunk.fulfilled, (state, action) => {
            const skuId = action.meta.arg.skuId
            state.bySku[skuId].currentRequestId = undefined
            state.bySku[skuId].data = action.payload.claims.map((claim: Claim) => claim.id)
            const { claims } = action.payload
            claims.forEach(claim => {
                state.entities[claim.id] = {
                    data: claim,
                    lastFetched: Date.now()
                }
            })
        })
        builder.addCase(skuClaimsThunk.rejected, (state, action) => {
            const skuId = action.meta.arg.skuId
            if (state.bySku[skuId]?.currentRequestId !== action.meta.requestId)
                return
            state.bySku[skuId].currentRequestId = undefined
        })             

        builder.addCase(activePlansThunk.fulfilled, (state, action) => {
            const { claims } = action.payload
            claims.forEach(claim => {
                state.entities[claim.id] = {
                    data: claim,
                    lastFetched: Date.now()
                }
            })
        })
        builder.addCase(storeSkuThunk.fulfilled, (state, action) => {
            const { claims } = action.payload
            claims.forEach(claim => {
                state.entities[claim.id] = {
                    data: claim,
                    lastFetched: Date.now()
                }
            })
            indexClaimsBySku(state, [action.meta.arg.id], claims)
        })
        builder.addCase(storeSkusThunk.fulfilled, (state, action) => {
            const { claims } = action.payload
            claims.forEach(claim => {
                state.entities[claim.id] = {
                    data: claim,
                    lastFetched: Date.now()
                }
            })
            indexClaimsBySku(state, action.payload.skus.map((sku: { id: string }) => sku.id), claims)
        })
        builder.addCase(skuThunk.fulfilled, (state, action) => {
            const { claims } = action.payload
            claims.forEach(claim => {
                state.entities[claim.id] = {
                    data: claim,
                    lastFetched: Date.now()
                }
            })
            indexClaimsBySku(state, [action.meta.arg.id], claims)
        })
        builder.addCase(subscriptionThunk.fulfilled, (state, action) => {
            const { claims } = action.payload
            claims.forEach(claim => {
                state.entities[claim.id] = {
                    data: claim,
                    lastFetched: Date.now()
                }
            })
        })
        builder.addCase(switchSubscriptionThunk.fulfilled, (state, action) => {
            const { claims } = action.payload
            claims.forEach(claim => {
                state.entities[claim.id] = {
                    data: claim,
                    lastFetched: Date.now()
                }
            })
        })
        builder.addCase(logOut, (state: ClaimsState) => {
            state = initialState;
        })
    },
})


// GET claims/:claimId

export const claimThunk = createAsyncThunk(
    'claims/fetchClaim',
    async ({ id }: { id: string }, { getState, requestId }) => {
        const state = getState() as RootState;   // avoid sending request if it's going to be discarded anyway
        const currentRequestId = state.claims.entities[id]?.currentRequestId
        if (currentRequestId !== requestId)
            throw new Error("Request outdated");

        return await Api.getClaim({ claimId: id })
    }
)

const claimEntity = (id: string) => {
    return {
        selector: (state: RootState) => state.claims.entities[id],
        action: claimThunk({ id })
    }
}

export function useClaim(id: string) {
    return useFetchableState<Claim>({
        selector: (state: RootState) => state.claims.entities[id],
        action: claimThunk({ id })
    })
}


// GET claims?code=:redemptionCode

export const codeClaimsThunk = createAsyncThunk(
    'claims/fetchCodeClaim',
    async ({ redemptionCode, accessToken }: { redemptionCode: string, accessToken: string }, { getState, requestId }) => {
        // const state = getState() as RootState;   // avoid sending request if it's going to be discarded anyway
        return await Api.getRedemptionCodeClaims({ redemptionCode, accessToken })
    }
)


// GET skus/:skuId/claims

export const skuClaimsThunk = createAsyncThunk(
    'claims/fetchSkuClaims',
    async ({ skuId, accessToken }: { skuId: string, accessToken: string }, { getState, requestId }) => {
        // const state = getState() as RootState;   // avoid sending request if it's going to be discarded anyway
        return await Api.getSkuClaims({ skuId, accessToken })
    }
)

export const useSkuClaims = (skuId: string, accessToken: string) => {
    return useFetchableState<string[]>({
        selector: (state: RootState) => state.claims.bySku[skuId],
        action: skuClaimsThunk({ skuId, accessToken })
    })
}

// Latest-version claim ids for a sku, fed by the public sku fetch — unlike
// useSkuClaims this needs no admin access, so user-facing surfaces (the
// upgrade popover) can use it.
export const useStoreSkuClaims = (skuId: string) => {
    return useFetchableState<string[]>({
        selector: (state: RootState) => state.claims.bySku[skuId],
        action: skuThunk({ id: skuId })
    })
}

export default claimsSlice.reducer